import * as React from "react";
import Box from "@mui/material/Box";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";

/**
 * Says whether the host being viewed is serving real traffic data or fabricated data
 * from the feed simulator.  See uic-gtis/travelmidwest-app#78.
 *
 * A tester could not tell before this: on 2026-09-10 testing81 served a Heat Advisory
 * and a Severe Thunderstorm Warning that exist in no forecast, and the only tells were
 * an alert id containing "sim.zone.0001" and a headline dated July.  A host mistaken
 * for real-time is one an operator may begin hand-editing, which corrupts a simulation
 * baseline with no record of having done so.
 *
 * Two rules this component exists to keep:
 *
 * 1. **The mode is never inferred.**  A request that fails, times out, returns a
 *    non-JSON body, or returns a mode this code does not recognise leaves the mode
 *    "unknown".  There is no path here that concludes "real-time" from the absence of
 *    evidence -- that is the failure this indicator exists to prevent, and the same
 *    rule gwlease follows on the host.
 * 2. **The element is always in the DOM**, carrying the mode and generation as data
 *    attributes, so the Selenium suite has a stable hook whatever the mode is
 *    (uic-gtis/Selenium_scripts).  Only the *banner* is conditional.
 *
 * The banner shows for "simulated" and nothing else.  Production reports "unknown",
 * because gwlease is not installed there, so a banner for "unknown" would put a notice
 * in front of every public visitor to say nothing useful.  Not claiming "real-time" is
 * what rule 1 requires; announcing uncertainty to the public is not.
 */
export default function SimulationModeIndicator() {
  const [state, setState] = React.useState({ mode: "unknown", generation: null });

  React.useEffect(() => {
    let cancelled = false;

    const read = async () => {
      // Deliberately no catch-to-real-time: every failure below leaves the initial
      // "unknown" state in place.
      const response = await fetch(process.env.REACT_APP_API_HOST + "/simulationMode.json");
      if (!response.ok) {
        return;
      }
      const body = await response.json();
      if (cancelled) {
        return;
      }
      setState({
        mode: KNOWN_MODES.includes(body.mode) ? body.mode : "unknown",
        generation: body.generation ?? null,
      });
    };

    read().catch(() => {
      // An unreachable or unparseable endpoint means the mode is unknown, which is
      // already what state holds.  Nothing to do, and nothing to tell the visitor.
    });

    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <Box
      data-testid="simulation-mode"
      data-mode={state.mode}
      // The generation changes on every verified mode transition, so a long test run
      // can compare it at the end and void a result that spans a change rather than
      // reporting a mixture of two environments.
      data-generation={state.generation ?? ""}
      role={state.mode === "simulated" ? "alert" : undefined}
      sx={
        state.mode === "simulated"
          ? {
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: 1,
              px: 2,
              py: 0.5,
              backgroundColor: "#8b0000",
              color: "#fff",
              fontWeight: 700,
              letterSpacing: "0.04em",
              textTransform: "uppercase",
              fontSize: "0.8rem",
            }
          : { display: "none" }
      }
    >
      {state.mode === "simulated" && (
        <>
          <WarningAmberIcon fontSize="small" aria-hidden="true" />
          <span>Simulated data &mdash; this page is not showing real traffic conditions</span>
        </>
      )}
    </Box>
  );
}

/** The only modes the endpoint asserts.  Anything else, including absent, is unknown. */
const KNOWN_MODES = ["real-time", "simulated", "unknown"];
